import { z } from "zod";
import { BusinessDate, Instant, Note } from "./common.js";
import { Id } from "./ids.js";
import { LocalTime } from "./orders.js";

/**
 * Distribution contracts (requirements §9.1–§9.6, architecture §7.2, §8.5(ج-۷)).
 *
 * All counts are whole numbers (integers).
 * Returnable container kardex is an immutable, append-only ledger (never edited, only new entries).
 * Driver and loading views are strictly scoped with no special portions or upgrades visible.
 */

// ---------------------------------------------------------------------------------------------------------------
// Core statuses and Route Templates

export const DeliveryStatus = z.enum(["delivered", "delivered-short", "failed"]);
export type DeliveryStatus = z.infer<typeof DeliveryStatus>;

export const DispatchStopStatus = z.enum(["pending", "on-route", "delivered", "delivered-short", "failed"]);
export type DispatchStopStatus = z.infer<typeof DispatchStopStatus>;

/** Route template stop item (manager-editable). */
export const RouteStopTemplate = z
  .object({
    id: Id,
    sequence: z.int().min(1),
    unitId: Id,
    expectedDropoffTime: LocalTime.optional(),
    notes: z.string().optional(),
  })
  .strict();
export type RouteStopTemplate = z.infer<typeof RouteStopTemplate>;

export const RouteStopTemplateInput = z
  .object({
    unitId: Id,
    sequence: z.int().min(1),
    expectedDropoffTime: LocalTime.optional(),
    notes: z.string().optional(),
  })
  .strict();
export type RouteStopTemplateInput = z.infer<typeof RouteStopTemplateInput>;

/** Route template per driver and meal (manager-editable). */
export const RouteTemplate = z
  .object({
    id: Id,
    name: z.string().min(1),
    driverId: Id,
    mealId: Id,
    vehicleNote: z.string().default(""),
    stops: z.array(RouteStopTemplate),
    active: z.boolean().default(true),
  })
  .strict();
export type RouteTemplate = z.infer<typeof RouteTemplate>;

export const RouteTemplateInput = z
  .object({
    name: z.string().min(1),
    driverId: Id,
    mealId: Id,
    vehicleNote: z.string().optional(),
    stops: z.array(RouteStopTemplateInput),
    active: z.boolean().optional(),
  })
  .strict();
export type RouteTemplateInput = z.infer<typeof RouteTemplateInput>;

// ---------------------------------------------------------------------------------------------------------------
// Loading Manifest (Strict: no upgrade/special portions or prices visible)

export const LoadingItem = z
  .object({
    orderLineId: Id,
    orderId: Id,
    unitId: Id,
    menuItemId: Id,
    tierId: Id.nullable(),
    orderedQuantity: z.int().min(1),
    packagingSiteId: Id,
    packagingSiteName: z.string().optional(),
    containerTypeId: Id.optional(),
    containerCount: z.int().nonnegative().optional(),
  })
  .strict();
export type LoadingItem = z.infer<typeof LoadingItem>;

export const SiteLoadingGroup = z
  .object({
    packagingSiteId: Id,
    packagingSiteName: z.string(),
    items: z.array(LoadingItem),
    totalPortions: z.int().nonnegative(),
  })
  .strict();
export type SiteLoadingGroup = z.infer<typeof SiteLoadingGroup>;

export const LoadingWarning = z
  .object({
    code: z.literal("MULTIPLE_LOADING_POINTS"),
    message: z.string(),
    siteIds: z.array(Id),
  })
  .strict();
export type LoadingWarning = z.infer<typeof LoadingWarning>;

// ---------------------------------------------------------------------------------------------------------------
// Delivery and Dispatch

export const DeliveryLineResult = z
  .object({
    orderLineId: Id,
    orderedQuantity: z.int().nonnegative(),
    deliveredQuantity: z.int().nonnegative(),
    /** orderedQuantity - deliveredQuantity; positive indicates short. */
    difference: z.int(),
  })
  .strict();
export type DeliveryLineResult = z.infer<typeof DeliveryLineResult>;

export const DeliveryResult = z
  .object({
    status: DeliveryStatus,
    deliveredLines: z.array(DeliveryLineResult),
    deliveredAt: Instant,
    failureReason: z.string().optional(),
    complaintNote: Note.optional(),
    recipientSignatureName: z.string().optional(),
    containersDelivered: z.record(z.string(), z.int().nonnegative()).optional(),
    containersReturned: z.record(z.string(), z.int().nonnegative()).optional(),
  })
  .strict();
export type DeliveryResult = z.infer<typeof DeliveryResult>;

export const DispatchStop = z
  .object({
    id: Id,
    sequence: z.int().min(1),
    unitId: Id,
    orderIds: z.array(Id),
    status: DispatchStopStatus,
    loadingItems: z.array(LoadingItem),
    deliveryResult: DeliveryResult.optional(),
    notes: z.string().optional(),
  })
  .strict();
export type DispatchStop = z.infer<typeof DispatchStop>;

export const Dispatch = z
  .object({
    id: Id,
    templateId: Id.nullable().optional(),
    businessDate: BusinessDate,
    mealId: Id,
    driverId: Id,
    stops: z.array(DispatchStop),
    loadingWarnings: z.array(LoadingWarning).default([]),
    startedAt: Instant.nullable().optional(),
    completedAt: Instant.nullable().optional(),
  })
  .strict();
export type Dispatch = z.infer<typeof Dispatch>;

// ---------------------------------------------------------------------------------------------------------------
// Returnable Containers & Kardex

export const ContainerType = z
  .object({
    id: Id,
    code: z.string().min(1),
    name: z.string().min(1),
    description: z.string().default(""),
    active: z.boolean().default(true),
  })
  .strict();
export type ContainerType = z.infer<typeof ContainerType>;

export const ContainerTypeInput = z
  .object({
    code: z.string().min(1),
    name: z.string().min(1),
    description: z.string().optional(),
    active: z.boolean().optional(),
  })
  .strict();
export type ContainerTypeInput = z.infer<typeof ContainerTypeInput>;

export const ContainerDirection = z.enum(["out", "in", "reversal"]);
export type ContainerDirection = z.infer<typeof ContainerDirection>;

export const ReturnableLedgerEntry = z
  .object({
    id: Id,
    unitId: Id,
    containerTypeId: Id,
    businessDate: BusinessDate,
    instant: Instant,
    direction: ContainerDirection,
    quantity: z.int().min(1),
    dispatchId: Id.nullable().optional(),
    driverId: Id.nullable().optional(),
    reversalOfEntryId: Id.nullable().optional(),
    reason: z.string().nullable().optional(),
  })
  .strict();
export type ReturnableLedgerEntry = z.infer<typeof ReturnableLedgerEntry>;

export const ContainerKardex = z
  .object({
    unitId: Id,
    containerTypeId: Id,
    totalOut: z.int().nonnegative(),
    totalIn: z.int().nonnegative(),
    balance: z.int(),
    overdueBalance: z.int().nonnegative(),
    entries: z.array(ReturnableLedgerEntry),
  })
  .strict();
export type ContainerKardex = z.infer<typeof ContainerKardex>;

export const UnitContainerBalance = z
  .object({
    unitId: Id,
    unitName: z.string(),
    balances: z.array(
      z
        .object({
          containerTypeId: Id,
          containerTypeCode: z.string(),
          containerTypeName: z.string(),
          totalOut: z.int().nonnegative(),
          totalIn: z.int().nonnegative(),
          balance: z.int(),
          overdueBalance: z.int().nonnegative(),
        })
        .strict(),
    ),
  })
  .strict();
export type UnitContainerBalance = z.infer<typeof UnitContainerBalance>;

// ---------------------------------------------------------------------------------------------------------------
// Delivery Problems (§9.6)

export const DeliveryProblemKind = z.enum(["late", "shortage", "quality", "wrong-container", "other"]);
export type DeliveryProblemKind = z.infer<typeof DeliveryProblemKind>;

export const DeliveryProblem = z
  .object({
    id: Id,
    businessDate: BusinessDate,
    mealId: Id,
    dispatchId: Id.nullable().optional(),
    stopId: Id.nullable().optional(),
    unitId: Id,
    orderId: Id.nullable().optional(),
    kind: DeliveryProblemKind,
    note: z.string().min(1),
    recordedByPersonId: Id.nullable().optional(),
    recordedAt: Instant,
    resolvedAt: Instant.nullable().optional(),
    resolutionNote: z.string().nullable().optional(),
  })
  .strict();
export type DeliveryProblem = z.infer<typeof DeliveryProblem>;

export const RecordDeliveryProblemInput = z
  .object({
    businessDate: BusinessDate,
    mealId: Id,
    dispatchId: Id.optional(),
    stopId: Id.optional(),
    unitId: Id,
    orderId: Id.optional(),
    kind: DeliveryProblemKind,
    note: z.string().min(1),
  })
  .strict();
export type RecordDeliveryProblemInput = z.infer<typeof RecordDeliveryProblemInput>;

// ---------------------------------------------------------------------------------------------------------------
// Driver Day Report

export const DriverDayReport = z
  .object({
    driverId: Id,
    businessDate: BusinessDate,
    totalStops: z.int().nonnegative(),
    completedStops: z.int().nonnegative(),
    failedStops: z.int().nonnegative(),
    totalOrderedPortions: z.int().nonnegative(),
    totalDeliveredPortions: z.int().nonnegative(),
    shortPortions: z.int().nonnegative(),
    failures: z.array(
      z.object({
        unitId: Id,
        reason: z.string(),
        deliveredAt: Instant,
      }),
    ),
    containersDelivered: z.record(z.string(), z.int().nonnegative()),
    containersReturned: z.record(z.string(), z.int().nonnegative()),
  })
  .strict();
export type DriverDayReport = z.infer<typeof DriverDayReport>;

// ---------------------------------------------------------------------------------------------------------------
// Driver Phone Views (Strict: No special portions, no upgrades, no prices)

export const DriverStopView = z
  .object({
    id: Id,
    sequence: z.int().min(1),
    unitId: Id,
    unitName: z.string(),
    unitAddress: z.string(),
    unitCoordinates: z.object({ lat: z.number(), lng: z.number() }).nullable().optional(),
    contactPersonName: z.string().optional(),
    contactPhone: z.string().optional(),
    deliveryNotes: z.string().optional(),
    status: DispatchStopStatus,
    orderIds: z.array(Id),
    items: z.array(
      z
        .object({
          orderLineId: Id,
          menuItemId: Id,
          foodName: z.string(),
          orderedQuantity: z.int().min(1),
          deliveredQuantity: z.int().nonnegative().optional(),
          containerTypeId: Id.optional(),
          containerCount: z.int().nonnegative().optional(),
        })
        .strict(),
    ),
    deliveryResult: DeliveryResult.optional(),
  })
  .strict();
export type DriverStopView = z.infer<typeof DriverStopView>;

export const DriverDispatchView = z
  .object({
    id: Id,
    businessDate: BusinessDate,
    mealId: Id,
    driverId: Id,
    driverName: z.string(),
    vehicleNote: z.string(),
    startedAt: Instant.nullable().optional(),
    completedAt: Instant.nullable().optional(),
    stops: z.array(DriverStopView),
    loadingGroups: z.array(SiteLoadingGroup),
    loadingWarnings: z.array(LoadingWarning),
  })
  .strict();
export type DriverDispatchView = z.infer<typeof DriverDispatchView>;

// ---------------------------------------------------------------------------------------------------------------
// Input Request Payloads

export const RecordDeliveryLineInput = z
  .object({
    orderLineId: Id,
    deliveredQuantity: z.int().nonnegative(),
  })
  .strict();
export type RecordDeliveryLineInput = z.infer<typeof RecordDeliveryLineInput>;

export const RecordDeliveryInput = z
  .object({
    status: DeliveryStatus.optional(),
    deliveredCounts: z.array(RecordDeliveryLineInput),
    failureReason: z.string().optional(),
    complaintNote: Note.optional(),
    recipientSignatureName: z.string().optional(),
    containersDelivered: z.record(z.string(), z.int().nonnegative()).optional(),
    containersReturned: z.record(z.string(), z.int().nonnegative()).optional(),
  })
  .strict();
export type RecordDeliveryInput = z.infer<typeof RecordDeliveryInput>;

export const MoveStopInput = z
  .object({
    newSequence: z.int().min(1),
  })
  .strict();
export type MoveStopInput = z.infer<typeof MoveStopInput>;

export const SwapDriverInput = z
  .object({
    newDriverId: Id,
  })
  .strict();
export type SwapDriverInput = z.infer<typeof SwapDriverInput>;

export const ReassignStopInput = z
  .object({
    targetDispatchId: Id,
    targetSequence: z.int().min(1).optional(),
  })
  .strict();
export type ReassignStopInput = z.infer<typeof ReassignStopInput>;

export const RecordContainerEntryInput = z
  .object({
    unitId: Id,
    containerTypeId: Id,
    businessDate: BusinessDate,
    direction: z.enum(["out", "in"]),
    quantity: z.int().min(1),
    dispatchId: Id.optional(),
    reason: z.string().optional(),
  })
  .strict();
export type RecordContainerEntryInput = z.infer<typeof RecordContainerEntryInput>;

export const ReverseContainerEntryInput = z
  .object({
    reason: z.string().min(1),
  })
  .strict();
export type ReverseContainerEntryInput = z.infer<typeof ReverseContainerEntryInput>;

// ---------------------------------------------------------------------------------------------------------------
// Domain Events

export const DISTRIBUTION_EVENTS = [
  "distribution.dispatch-assigned",
  "distribution.loading-completed",
  "distribution.stop-delivered",
  "distribution.stop-reassigned",
  "distribution.container-recorded",
  "distribution.problem-recorded",
] as const;
export type DistributionEvent = (typeof DISTRIBUTION_EVENTS)[number];

export const DistributionEventPayload = z
  .object({
    dispatchId: Id.optional(),
    businessDate: BusinessDate,
    mealId: Id,
    driverId: Id.optional(),
    stopId: Id.optional(),
    unitId: Id.optional(),
    status: z.string().optional(),
    urgent: z.boolean(),
  })
  .strict();
export type DistributionEventPayload = z.infer<typeof DistributionEventPayload>;
